// <banner-slider>: fading full-width slides with progress bars, a counter, a pause button and swipe.
// Markup: [data-slide] slides (first active, others aria-hidden + inert), [data-slide-to] bar buttons,
// [data-slider-toggle] pause/play, [data-slide-current] counter. Autoplay (data-autoplay, data-interval seconds) stops
// for reduced motion, pauses on hover/focus and while the tab is hidden. The progress bar animation is pure CSS.

// Next index for a step (+1 / -1), wrapping around.
export function wrapIndex(index, step, count) {
  return (index + step + count) % count;
}

// Swipe direction to slide step: a swipe towards the reading start goes forward.
export function swipeStep(deltaX, rtl, threshold = 40) {
  if (Math.abs(deltaX) < threshold) return 0;
  const forward = rtl ? deltaX > 0 : deltaX < 0;
  return forward ? 1 : -1;
}

export class BannerSlider extends (globalThis.HTMLElement ?? class {}) {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-slide]')];
    if (this.slides.length < 2 || this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.index = 0;
    this.bars = [...this.querySelectorAll('[data-slide-to]')];
    this.counter = this.querySelector('[data-slide-current]');
    // Two-digit counter in the page's digits (Arabic-Indic on Arabic pages, as the server renders the first value).
    const arabic = document.documentElement.lang.startsWith('ar');
    this.digits = new Intl.NumberFormat(arabic ? 'ar' : 'en', { minimumIntegerDigits: 2, useGrouping: false, numberingSystem: arabic ? 'arab' : 'latn' });
    this.toggle = this.querySelector('[data-slider-toggle]');
    // The other slides' images wait for the page load (CSS hides their media until data-media-ready), so they never
    // compete with the first slide's image, the page's largest paint.
    const mediaReady = () => this.setAttribute('data-media-ready', '');
    if (document.readyState === 'complete') mediaReady(); else addEventListener('load', mediaReady, { once: true, signal });
    this.reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.autoplay = this.dataset.autoplay === 'true' && !this.reduced;
    this.interval = Math.max(3, Number(this.dataset.interval || 6)) * 1000;
    this.userPaused = !this.autoplay;
    this.remaining = this.interval;
    this.bars.forEach((bar, i) => bar.addEventListener('click', () => this.show(i), { signal }));
    if (this.toggle && this.reduced) this.toggle.hidden = true;
    this.toggle?.addEventListener('click', () => { this.userPaused = !this.userPaused; this.sync(); }, { signal });
    for (const [type, value] of [['pointerenter', true], ['pointerleave', false], ['focusin', true], ['focusout', false]]) {
      this.addEventListener(type, (event) => {
        if (type === 'focusout' && this.contains(event.relatedTarget)) return;
        this.hovered = value; this.sync();
      }, { signal });
    }
    document.addEventListener('visibilitychange', () => this.sync(), { signal });
    let startX = null;
    this.addEventListener('pointerdown', (event) => { if (event.pointerType !== 'mouse') startX = event.clientX; }, { signal });
    this.addEventListener('pointerup', (event) => {
      if (startX === null) return;
      const step = swipeStep(event.clientX - startX, getComputedStyle(this).direction === 'rtl');
      startX = null;
      if (step) this.show(wrapIndex(this.index, step, this.slides.length));
    }, { signal });
    // Theme editor: show the selected slide and stop rotating while editing it.
    document.addEventListener('shopify:block:select', (event) => {
      const i = this.slides.findIndex((slide) => slide.contains(event.target) || slide === event.target);
      if (i >= 0) { this.editing = true; this.show(i); }
    }, { signal });
    document.addEventListener('shopify:block:deselect', () => { this.editing = false; this.sync(); }, { signal });
    this.sync();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    clearTimeout(this.timer);
  }

  get running() {
    return this.autoplay && !this.userPaused && !this.hovered && !this.editing && document.visibilityState === 'visible';
  }

  show(next) {
    if (next === this.index) return this.sync();
    this.setAttribute('data-media-ready', '');
    this.slides.forEach((slide, i) => {
      const active = i === next;
      slide.toggleAttribute('data-active', active);
      slide.setAttribute('aria-hidden', String(!active));
      slide.inert = !active;
    });
    this.bars.forEach((bar, i) => {
      bar.toggleAttribute('data-active', i === next);
      bar.toggleAttribute('data-done', i < next);
      if (i === next) bar.setAttribute('aria-current', 'true'); else bar.removeAttribute('aria-current');
    });
    this.index = next;
    this.remaining = this.interval;
    this.started = null;
    if (this.counter) this.counter.textContent = this.digits.format(next + 1);
    // Restart the active bar's CSS fill.
    const bar = this.bars[next];
    if (bar) { bar.removeAttribute('data-active'); void bar.offsetWidth; bar.setAttribute('data-active', ''); }
    this.sync();
  }

  sync() {
    clearTimeout(this.timer);
    // Keep the timer in step with the CSS progress bar, which pauses with [data-paused].
    if (this.started) this.remaining = Math.max(0, this.remaining - (Date.now() - this.started));
    this.started = null;
    const running = this.running;
    this.toggleAttribute('data-paused', !running);
    if (this.toggle) {
      this.toggle.setAttribute('aria-pressed', String(this.userPaused));
      this.toggle.setAttribute('aria-label', this.toggle.dataset.pauseLabel);
    }
    if (running) {
      this.started = Date.now();
      this.timer = setTimeout(() => this.show(wrapIndex(this.index, 1, this.slides.length)), this.remaining);
    }
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('banner-slider')) customElements.define('banner-slider', BannerSlider);
